<template>
    <div class="actionables-page">
        <BreadcrumbsProject
        v-if="$route.meta.layout !== 'deluxe'"
        :breadcrumbs="[
            {
                link : dotnetUrl + '/project/list',
                title : $t('Projects'),
                isRouterLink: false    
            },
            {
                link : '/Project/' + projectInfo._id,
                title : (projectInfo.projectNumber || '-') +' ' + (projectInfo.projectName || ''),
                isRouterLink: true
            },
            {
                link : '/Project/' + projectInfo._id  + '/actionables',
                title : $t('Actionables'),
                isRouterLink: true
            },
        ]"
        ></BreadcrumbsProject>

        <div class="actionables-page__wrap">
            <nav v-if="$route.meta.layout === 'deluxe'" class="actionables-page__crumbs" aria-label="Breadcrumb">
                <a :href="dotnetUrl + '/project/list'">{{ $t('Projects') }}</a>
                <span>/</span>
                <router-link :to="'/Project/' + projectId">{{ projectTitle }}</router-link>
                <span>/</span>
                <span>{{ $t('Actionables') }}</span>
            </nav>

            <router-link class="actionables-page__back" :to="'/Project/' + projectId">
                <i class="fa-solid fa-chevron-left"></i>
                {{ $t('Back to Project Info') }}
            </router-link>

            <section class="actionables-panel">
                <header class="actionables-panel__header">
                    <div>
                        <p class="actionables-panel__eyebrow">{{ $t('PROJECT ACTIONABLES') }}</p>
                        <h1>{{ $t('Actionables') }}</h1>
                    </div>
                    <div class="actionables-panel__project">
                        <span>{{ projectInfo.projectNumber || '-' }}</span>
                        <strong>{{ projectInfo.projectName || '' }}</strong>
                    </div>
                </header>

                <div class="actionables-panel__body">
                    <div v-if="!data.length" class="actionables-empty">
                        <span class="actionables-empty__icon">
                            <i class="fa-solid fa-circle-check"></i>
                        </span>
                        <div>
                            <strong>{{ $t('No actionables available') }}</strong>
                            <p>{{ $t('There are no outstanding project actionables at the moment.') }}</p>
                        </div>
                    </div>

                    <section
                        v-for="group in groupedActionables"
                        :key="group.module.key"
                        class="actionables-module-group"
                    >
                        <h2 class="actionables-module-group__title">
                            <span></span>
                            {{ $t(group.module.label) }}
                        </h2>

                        <article
                            v-for="(item, key) in group.items"
                            :key="moduleItemKey(group.module.key, key, item)"
                            :class="['actionables-item', { 'is-complete': item.status, 'is-pending': !item.status }]"
                        >
                            <span
                                v-if="group.module.svg"
                                class="actionables-item__icon"
                                :title="$t(group.module.label)"
                                v-html="group.module.svg"
                            ></span>
                            <span v-else class="actionables-item__icon" :title="$t(group.module.label)">
                                <i :class="group.module.icon"></i>
                            </span>
                            <div class="actionables-item__message" :class="{'strikethrough': item.status}" v-html="item.message"></div>
                            <span class="actionables-item__status">{{ $t(item.status ? 'Completed' : 'Pending') }}</span>
                        </article>
                    </section>
                </div>
            </section>
        </div>
    </div>
</template>
<style scoped>
    :global(.content.actionables-deluxe-page .content__top .deluxe-breadcrumbs) {
        visibility: hidden;
    }

    :global(.deluxe-layout .content__wrapper.actionables-deluxe-page) {
        padding-top: 0.75rem;
    }

    .actionables-page {
        min-height: calc(100vh - 7rem);
        padding: 0 1rem 3rem;
        background: #f7f9f7;
        font-family: "Albert Sans", Arial, sans-serif;
    }

    .actionables-page__wrap {
        width: min(100%, 64.25rem);
        margin: 0 auto;
    }

    .actionables-page__crumbs {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.45rem;
        margin-bottom: 0.7rem;
        color: #777777;
        font-size: 0.82rem;
        line-height: 1.3;
    }

    .actionables-page__crumbs a,
    .actionables-page__crumbs span {
        color: #777777;
        text-decoration: none;
    }

    .actionables-page__crumbs span:last-child {
        color: #15865f;
        font-weight: 800;
    }

    .actionables-page__crumbs a:hover,
    .actionables-page__crumbs a:focus {
        color: #15865f;
        text-decoration: none;
    }

    .actionables-page__back {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        margin-bottom: 0.85rem;
        color: #51615b;
        font-size: 0.82rem;
        font-weight: 700;
        text-decoration: none;
    }

    .actionables-page__back:hover,
    .actionables-page__back:focus {
        color: #16825f;
        text-decoration: none;
    }

    .actionables-page__back i {
        font-size: 0.72rem;
    }

    .actionables-panel {
        overflow: hidden;
        border: 1px solid #238a67;
        border-radius: 0.45rem;
        background: #ffffff;
        box-shadow: 0 1.25rem 2.75rem rgba(20, 84, 63, 0.08);
    }

    .actionables-panel__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1.25rem;
        padding: 1rem 1.25rem 0.95rem;
        border-bottom: 1px solid #238a67;
        background: #238a67;
    }

    .actionables-panel__eyebrow {
        margin: 0;
        color: rgba(255, 255, 255, 0.82);
        font-size: 0.72rem;
        font-weight: 800;
        letter-spacing: 0;
    }

    .actionables-panel__header h1 {
        margin: 0.1rem 0 0;
        color: #ffffff;
        font-size: 1.35rem;
        font-weight: 800;
        line-height: 1.15;
    }

    .actionables-panel__project {
        display: grid;
        justify-items: end;
        color: #ffffff;
        font-size: 0.84rem;
        line-height: 1.2;
    }

    .actionables-panel__project span {
        color: rgba(255, 255, 255, 0.82);
        font-size: 0.78rem;
        font-weight: 700;
    }

    .actionables-panel__project strong {
        font-size: 0.95rem;
        font-weight: 800;
    }

    .actionables-panel__body {
        display: flex;
        flex-direction: column;
        gap: 0.9rem;
        padding: 1rem;
        background: #fbfdfc;
    }

    .actionables-module-group {
        display: flex;
        flex-direction: column;
        gap: 0.45rem;
    }

    .actionables-module-group__title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0.25rem 0 0;
        color: #061f18;
        font-size: 0.9rem;
        font-weight: 800;
        line-height: 1.25;
    }

    .actionables-module-group__title span {
        display: inline-flex;
        width: 0.45rem;
        height: 0.45rem;
        border-radius: 999px;
        background: #1c9a70;
    }

    .actionables-item {
        display: grid;
        grid-template-columns: 1.55rem minmax(0, 1fr) auto;
        align-items: center;
        gap: 0.8rem;
        min-height: 3.15rem;
        border: 1px solid #a7dfc7;
        border-radius: 0.4rem;
        padding: 0.65rem 0.85rem;
        background: #f1fbf6;
        color: #082b20;
        box-shadow: none;
    }

    .actionables-item__icon {
        display: inline-flex;
        width: 1.45rem;
        height: 1.45rem;
        align-items: center;
        justify-content: center;
        color: #1e1b16;
        font-size: 0.98rem;
    }

    .actionables-item__icon :deep(svg) {
        display: block;
        width: 1.15rem;
        height: 1.15rem;
    }

    .actionables-item__message {
        min-width: 0;
        color: #082b20;
        font-size: 0.88rem;
        font-weight: 700;
        line-height: 1.45;
    }

    .actionables-item__message :deep(a) {
        color: #07845f;
        font-weight: 800;
        text-decoration: underline;
        text-underline-offset: 0.12rem;
    }

    .actionables-item__status {
        display: inline-flex;
        min-height: 1.85rem;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        padding: 0.35rem 0.85rem;
        color: #ffffff;
        background: #4dce92;
        font-size: 0.78rem;
        font-weight: 800;
        white-space: nowrap;
    }

    .actionables-item.is-complete {
        border-color: #bae8d4;
        background: #effaf5;
    }

    .actionables-item.is-pending {
        border-color: #75cda9;
        background: #ffffff;
    }

    .actionables-item.is-pending .actionables-item__status {
        border: 1px solid #1c9a70;
        background: #ffffff;
        color: #15865f;
    }

    .strikethrough {
        opacity: 0.86;
        text-decoration: line-through;
    }

    .actionables-empty {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        border: 1px dashed #99dec3;
        border-radius: 0.35rem;
        padding: 1rem;
        color: #174437;
        background: #effaf5;
    }

    .actionables-empty__icon {
        display: inline-flex;
        width: 2.25rem;
        height: 2.25rem;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #ffffff;
        background: #22a675;
    }

    .actionables-empty strong {
        display: block;
        margin-bottom: 0.15rem;
        font-weight: 800;
    }

    .actionables-empty p {
        margin: 0;
        color: #52665f;
        font-size: 0.85rem;
    }

    @media (max-width: 767.98px) {
        :global(.deluxe-layout .content__wrapper.actionables-deluxe-page) {
            padding: 0.75rem 0.75rem 1.5rem;
        }

        .actionables-page {
            padding: 0 0 2rem;
        }

        .actionables-panel__header,
        .actionables-item {
            grid-template-columns: 1fr;
        }

        .actionables-panel__header {
            align-items: stretch;
            flex-direction: column;
        }

        .actionables-panel__project {
            justify-items: start;
        }

        .actionables-item {
            align-items: start;
        }

        .actionables-item__status {
            width: max-content;
        }
    }
</style>

<script>
import {mapGetters, mapActions} from 'vuex'
import {LinkList} from '@/helpers/utilities.js'
import BreadcrumbsProject from '@/components/projectInformation/BreadcrumbsProject.vue'
import { sidebarSvg } from '@/components/deluxe/DeluxeSidebar.vue'
import {DashboardGetProjectActionablesFirstPart,DashboardGetProjectActionables} from '@/actions/projectActions.js'
import 'dotenv/config'

const ACTIONABLE_MODULES = [
    {
        key: 'change-request',
        label: 'Change Request',
        svg: sidebarSvg.changeRequest,
        matches: ['change order', 'change request']
    },
    {
        key: 'client-invoice',
        label: 'Client Invoice',
        svg: sidebarSvg.clientInvoice,
        matches: ['client invoice', 'invoice']
    },
    {
        key: 'progress-claim',
        label: 'Progress Claim',
        svg: sidebarSvg.progressClaim,
        matches: ['progress claim', 'progress claims']
    },
    {
        key: 'purchase-order',
        label: 'Purchase Order',
        svg: sidebarSvg.purchaseOrder,
        matches: ['purchase order', 'purchase orders', ' po-']
    },
    {
        key: 'procurement',
        label: 'Procurement',
        svg: sidebarSvg.procurement,
        matches: ['procurement', 'procured', 'trade package', 'trade packages']
    },
    {
        key: 'quotation',
        label: 'Quotation',
        svg: sidebarSvg.quotation,
        matches: ['quotation', 'quotations']
    },
    {
        key: 'bill-of-quantity',
        label: 'Bill Of Quantity',
        svg: sidebarSvg.boq,
        matches: ['bill of quantity', 'boq']
    },
    {
        key: 'project-directory',
        label: 'Project Directory',
        svg: sidebarSvg.directory,
        matches: ['project directory', 'directory', 'project director', 'contact person', 'client found']
    },
    {
        key: 'contingency',
        label: 'Contingency',
        svg: sidebarSvg.contingency,
        matches: ['contingency']
    },
    {
        key: 'vendor-list',
        label: 'Vendor List',
        svg: sidebarSvg.vendorList,
        matches: ['vendor']
    }
]

const DEFAULT_ACTIONABLE_MODULE = {
    key: 'general',
    label: 'General',
    svg: sidebarSvg.projectDetails
}

const stripHtml = (value = '') => String(value).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

const resolveActionableModule = (item = {}) => {
    const moduleHint = item.module || item.moduleName || item.type || ''
    const searchableText = `${moduleHint} ${stripHtml(item.message)}`.toLowerCase()

    return ACTIONABLE_MODULES.find((module) => module.matches.some((match) => searchableText.includes(match))) || DEFAULT_ACTIONABLE_MODULE
}

export default {
    name : 'ActionableList',
    components : {
        BreadcrumbsProject
    },
    computed : {
        ...mapGetters(['projectInfo']),
        projectTitle() {
            return `${this.projectInfo.projectNumber || '-'} ${this.projectInfo.projectName || ''}`.trim()
        },
        groupedActionables() {
            const groups = []
            const groupMap = new Map()

            this.data.forEach((item) => {
                const module = resolveActionableModule(item)

                if (!groupMap.has(module.key)) {
                    const group = {
                        module,
                        items: []
                    }
                    groupMap.set(module.key, group)
                    groups.push(group)
                }

                groupMap.get(module.key).items.push(item)
            })

            return groups
        }
    },
    methods : {
        ...mapActions(['GetProjectInfo','ChangeActiveLink']),
        moduleItemKey(moduleKey, index, item) {
            return `${moduleKey}-${item.id || item._id || index}`
        },
    },
    mounted: async function(){
        const self = this
        self.ChangeActiveLink(LinkList.SUMMARY)
        await self.GetProjectInfo(self.projectId)
        const getData = await DashboardGetProjectActionablesFirstPart(self.projectId)
        let data = getData && Array.isArray(getData.messages) ? [...getData.messages] : []

        if(getData && getData.cleared){
            const getDotNetData = await DashboardGetProjectActionables(self.projectId)
            if(getDotNetData)
                data.push(...getDotNetData.data)
        }
        self.data = data.reverse()
    },
    data(){
        return {
            data : [],
            projectId : this.$route.params.id,
            dotnetUrl : process.env.VUE_APP_DOTNET_URL
        }
    }
}
</script>
